const firebase = require('firebase/app');
require('firebase/analytics');
require('firebase/auth');
require('firebase/firestore');

/*
Auth error list: https://firebase.google.com/docs/auth/admin/errors
 */

// Prevent multiple instances of FirebaseStorage
let IN_USE = false;
// Prevent saving while another save process is running
let SAVE_LOCK = false;
// Prevent loading user data for a user that was just created
let JUST_CREATED = false;

// This is used to create a pseudo password-less login
// IMPORTANT: Don't change this value
const PASSWORD = 'secret';

window.FirebaseStorage = Component.extend({

    initialize: function(config) {
        Logger.info(
            '%cFirebaseStorage.initialize',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );

        if (!config) {
            throw new Error('Firebase config is required');
        }

        if (IN_USE) {
            throw new Error(
                'You cannot start multiple instances of Firebase Storage. Check that container components does not duplicate FirebaseStorage');
        }
        IN_USE = true;

        this.name = 'FirebaseStorage';
        this.user = null;
        this.uid = null;
        this.container = null;
        this.config = config;

        /** @type {LearningLockerStorage} */
        this.remoteStorageManager = null;
    },

    lang() {
        Logger.info(
            '%cFirebaseStorage.lang',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        return {
            'auth/user-not-found': LanguageManager
                .getString('auth_user_not_found'),
            'auth/invalid-email': LanguageManager
                .getString('validation_email_format'),
            'auth/email-already-in-use': LanguageManager
                .getString('auth_email_already_in_use'),
        };
    },

    setup: function(container) {
        this.container = container;
        Logger.info(
            '%cFirebaseStorage.setup',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
            {
                rsm: this.config.remoteStorageManager,
                config: this.config,
                componentList: this.container.componentList,
            },
        );
        this.remoteStorageManager = this.container.componentList[this.config.remoteStorageManager];
    },

    doFirebaseStart: function() {
        Logger.info(
            '%cFirebaseStorage.doFirebaseStart',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        // Initialize Firebase
        firebase.initializeApp(this.config);
        firebase.analytics();
        firebase.auth().onAuthStateChanged(async user => {

            this.trigger('onFirebaseAuthStateChanged', { user });
            Logger.info('trigger :: onFirebaseAuthStateChanged', { user });

            // User is signed out
            if (!user) {
                this.user = null;
                this.uid = null;
                return;
            }

            this.user = user;
            this.uid = user.uid;

            // Do not try to load the user data if it is newly created
            if (!JUST_CREATED) {
                await this.load();
            }

            // Reset the just created lock
            JUST_CREATED = false;

            // tell SkillsManager to set skills
            this.container.componentList.SkillsManager.load();
        });
    },

    check() {
        Logger.info(
            '%cFirebaseStorage.check',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        // Is there a user on here already, anon or not
        return !!this.user;
    },

    async createWithEmail(email) {
        Logger.info(
            '%cFirebaseStorage.createWithEmail',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );

        // Prevent onAuthStatusChange from trying to load the data for the user
        // that is about to be created. Without this, duplicate users may be
        // created
        JUST_CREATED = true;
        return firebase.auth()
            .createUserWithEmailAndPassword(email, PASSWORD)
            .then(() => {
                this.trigger('onUserCreated', {
                    target: this,
                    type: 'email',
                });

                return this.save();
            })
            .catch(error => {
                Logger.error(`firebase.email :: ${ error.code }: ${ error.message }`);
                throw new Error(this.lang()[error.code] ||
                    LanguageManager.getString('auth_unknown_error'));
            });
    },

    async loginWithEmail(email) {
        Logger.info(
            '%cFirebaseStorage.loginWithEmail',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        // A user has already created a profile
        return firebase.auth()
            .signInWithEmailAndPassword(email, PASSWORD)
            .catch(error => {
                Logger.error(`firebase.email :: ${ error.code }: ${ error.message }`);
                throw new Error(this.lang()[error.code] ||
                    LanguageManager.getString('auth_unknown_error'));
            })
            .then(() => {
                this.trigger('onUserLoggedIn', {
                    target: this,
                    type: 'email',
                });
            });

        // return response;
    },

    setUserProperty: function(property, value) {
        Logger.info(
            '%cFirebaseStorage.setUserProperty',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );

        Logger.info('FirebaseStoreage.setUserProperty', property, value);

        if (property === 'email') {
            value = value.toLowerCase();
        }

        this.remoteStorageManager.setAttribute(property, value);

        this.trigger('onUserPropertyUpdated', {
            target: this,
            user: this.data(),
        });

    },

    async logout() {
        Logger.info(
            '%cFirebaseStorage.logout',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        this.trigger('userSignOut');

        Logger.info('Firebase.logout.before');
        const response = await firebase.auth().signOut();
        this.remoteStorageManager.clear();

        this.trigger('afterUserSignOut'); // Will remove
        this.trigger('onUserSignOut', {
            target: this,
        });
        Logger.info('-- Navigation.Fire.signed.out');
        return response;
    },

    syncTracking() {
        Logger.info(
            '%cFirebaseStorage.syncTracking',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        const agent = this.container.componentList.LRSConnector.agent;
    },

    async load() {
        Logger.info(
            '%cFirebaseStorage.load',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        this.trigger('userDataLoad');

        const filter = [
            'age', 'agreeDataShareUnion', 'agreeTerms', 'dateCreated',
            'dateLastAccess', 'email', 'firstName', 'lastName', 'location',
            'union',
        ];

        // instead of loading data from firbase, load from LRS
        await this.remoteStorageManager.load(this.user.email, filter);

        Logger.info('#########################################################');
        Logger.info('# data ##################################################');
        Logger.info('#########################################################');

        // remains the same
        this.trigger('afterUserDataLoad', {
            target: this,
            user: this.data(),
        });

        return this.data();
    },

    /**
     * @inheritDoc LearningLockerStorage.asObject
     */
    data() {
        Logger.info(
            '%cFirebaseStorage.data',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        return this.remoteStorageManager.asObject();
    },

    currentUser() {
        Logger.info(
            '%cFirebaseStorage.currentUser',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        return firebase.auth().currentUser;
    },

    remote() {
        Logger.info(
            '%cFirebaseStorage.remote',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );
        const db = firebase.firestore();
        return db.collection('users').doc(this.uid);
    },

    async save() {
        Logger.info(
            '%cFirebaseStorage.save',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
            { SAVE_LOCK },
        );
        if (!this.currentUser()) {
            return false;
        }

        if (SAVE_LOCK) {
            return false;
        }
        SAVE_LOCK = true;

        const rsmData = this.remoteStorageManager.data();

        if (rsmData.email.changed && !JUST_CREATED) {
            Logger.info('email has changed');

            // Email changing has been disabled until it is fixed
            // If a user has somehow changed their email address before submitting, change it back
            // rsmData.email.value = rsmData.email.original;

            const credentials = firebase.auth.EmailAuthProvider.credential(
                rsmData.email.original,
                PASSWORD,
            );
            await this.user.reauthenticateWithCredential(credentials);

            const res = await this.user.updateEmail(rsmData.email.value)
                .catch(error => error);

            Logger.info(res);

            if (res?.code) {
                const text = this.lang()[res.code] || LanguageManager.getString('auth_unknown_error')
                window.bootbox.alert(`<p style="color: red; margin-top: 1em;">${text}</p>`)
                SAVE_LOCK = false;
                return false;
            }
        }

        Logger.info(
            '%cFirebaseStorage.save :: passed check',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );

        const response = await this.remoteStorageManager.save();
        Logger.info(
            '%cFirebaseStorage.save :: saved data with LL',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
            { response },
        );

        this.trigger('onSave');
        Logger.info(
            '%cFirebaseStorage.save :: triggered event',
            'background: lightgrey; color: darkorange; padding: 0.2em 1em;',
        );

        // Finish saving. Unlock for next save.
        SAVE_LOCK = false;
        return true;
    },
});